import React, { useState, useRef, useCallback } from 'react'
import { useCreateTodo } from '../api/hooks'
import { parseInput, formatDate } from '../lib/parse'
import { todoId } from '../lib/ulid'
import { atEnd } from '../lib/fracidx'
import { useToast } from './primitives/Toast'
import type { Todo } from '../api/types'

interface TodoInputProps {
  /** Default date to assign to new todos (YYYY-MM-DD) */
  defaultDate?: string
  /** Default section to assign */
  defaultSectionId?: string
  /** Current todos in this column (used to compute next position) */
  todos?: Todo[]
  /** Placeholder text */
  placeholder?: string
  onCreated?: () => void
}

export function TodoInput({
  defaultDate,
  defaultSectionId,
  todos = [],
  placeholder = '+ Add task...',
  onCreated,
}: TodoInputProps) {
  const [value, setValue] = useState('')
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const createTodo = useCreateTodo()
  const { showToast } = useToast()

  const parsed = value.trim() ? parseInput(value) : null

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && value.trim()) {
        e.preventDefault()

        const input = parseInput(value)
        const text = input.text.trim()
        if (!text) return

        const maxPos = todos.length > 0 ? Math.max(...todos.map((t) => t.position)) : 0
        const position = atEnd(maxPos)

        // Resolve date: parsed date > defaultDate > null
        let resolvedDate = input.date
          ? formatDate(input.date)
          : defaultDate ?? null

        // "!9am" past 9am rolls to tomorrow, and the task belongs on the day it
        // will actually fire. This only applies when neither the text nor the
        // column supplied a date — typing "in 2 hours" into Friday's column
        // must stay on Friday.
        if (input.reminderAt && !input.date && !defaultDate) {
          resolvedDate = formatDate(input.reminderAt)
        }

        createTodo.mutate(
          {
            id: todoId(),
            text,
            status: 'todo',
            date: resolvedDate,
            section_id: defaultSectionId ?? null,
            reminder_at: input.reminderAt?.toISOString() ?? null,
            position,
          },
          {
            onError: () => showToast('Failed to create task', 'error'),
            onSuccess: () => onCreated?.(),
          }
        )

        setValue('')
      } else if (e.key === 'Escape') {
        setValue('')
        inputRef.current?.blur()
      }
    },
    [value, todos, defaultDate, defaultSectionId, createTodo, showToast, onCreated]
  )

  return (
    <div style={{ marginTop: '4px' }}>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          borderBottom: `1px solid ${focused && value ? 'var(--color-accent)' : focused ? 'var(--color-border)' : 'transparent'}`,
          outline: 'none',
          fontFamily: 'var(--font-serif)',
          fontSize: '0.875rem',
          color: 'var(--color-ink)',
          padding: '4px 0',
          transition: 'border-color 0.15s',
        }}
      />

      {/* Parse chips */}
      {focused && parsed && (
        <div
          style={{
            display: 'flex',
            gap: '4px',
            flexWrap: 'wrap',
            marginTop: '4px',
          }}
        >
          {parsed.date && (
            <Chip
              label={parsed.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              color="var(--color-accent)"
            />
          )}
          {parsed.sectionHint && (
            <Chip label={`@${parsed.sectionHint}`} color="var(--color-ink-muted)" />
          )}
          {parsed.reminderText && (
            <Chip label={`◷ ${parsed.reminderText}`} color="var(--color-accent-light)" />
          )}
          {parsed.text !== value.trim() && (
            <Chip label={parsed.text} color="var(--color-ink-faint)" />
          )}
        </div>
      )}
    </div>
  )
}

function Chip({ label, color }: { label: string; color: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 7px',
        borderRadius: '999px',
        border: `1px solid ${color}`,
        color,
        fontSize: '0.75rem',
        fontFamily: 'var(--font-mono)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  )
}
